import { useEffect, useState } from "react";
import { HubConnectionBuilder } from "@microsoft/signalr";
import { Avatar, Tooltip } from "@mantine/core";
import UseUser from "@/hooks/useUser";
import { useAuth0 } from "@auth0/auth0-react";

interface ViewerUser {
  userId: string;
  name: string;
  email: string;
}

const VIEWER_COUNT = 4;

const ViewerComponent = ({
  leadId,
  color = "cyan",
  avatarSize = undefined,
}: {
  leadId: string;
  visual?: boolean;
  color: string;
  avatarSize?: number;
}) => {
  const { isLoading, finalUser } = UseUser();
  const { getAccessTokenSilently } = useAuth0();
  const [viewerMap, setViewerMap] = useState<Map<string, ViewerUser>>(
    new Map(),
  );

  const viewerList = Array.from(viewerMap.values());

  useEffect(() => {
    const connection = new HubConnectionBuilder()
      .withUrl(
        `${import.meta.env.VITE_PUBLIC_API_URL}/api/viewerHub?leadId=${leadId}`,
        {
          withCredentials: true,
          accessTokenFactory: async () => {
            const token = await getAccessTokenSilently();
            return token;
          },
        },
      )
      .withAutomaticReconnect()
      .build();

    const connect = () => {
      if (isLoading) return;

      connection.on("ViewerJoined", (user: ViewerUser) => {
        setViewerMap((prev) => new Map(prev).set(user.userId, user));
      });

      connection.on("ViewerLeft", (userId: string) => {
        setViewerMap((prev) => {
          const next = new Map(prev);
          next.delete(userId);
          return next;
        });
      });

      connection.on("AnnounceRequested", (announcedLeadId: string) => {
        connection
          .invoke("AnnouncePresence", announcedLeadId)
          .catch((err) =>
            console.error("SignalR AnnouncePresence Error:", err),
          );
      });

      connection
        .start()
        .catch((err) => console.error("SignalR Connection Error:", err));
    };

    void connect();

    return () => {
      if (!connection) return;
      connection
        .invoke("LeaveViewer", leadId)
        .catch((err) => {
          console.error("SignalR LeaveViewer Error:", err);
        })
        .finally(() => {
          void connection.stop();
        });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalUser]);

  const getInitials = (name: string) => {
    const nameArray = name.split(" ");
    const initials =
      nameArray.length > 1
        ? nameArray[0]?.charAt(0) + (nameArray[1] ? nameArray[1].charAt(0) : "")
        : nameArray[0]?.charAt(0);
    return initials ? initials.toUpperCase() : "";
  };

  const generateViewerListTooltip = () => {
    return viewerList
      .slice(VIEWER_COUNT - 1)
      .map((user) => `${user.name} (${user.email})`)
      .join(", ");
  };

  return (
    <div style={{ display: "flex", flexWrap: "wrap" }} key={"viewerList"}>
      {viewerList.length <= VIEWER_COUNT ? (
        viewerList.map((user) => (
          <Tooltip label={`${user.name} (${user.email})`} key={user.userId}>
            <Avatar color={color} radius="xl" mr={4} size={avatarSize}>
              {getInitials(user.name)}
            </Avatar>
          </Tooltip>
        ))
      ) : (
        <>
          {viewerList.slice(0, VIEWER_COUNT - 1).map((user) => (
            <Tooltip label={`${user.name} (${user.email})`} key={user.userId}>
              <Avatar color={color} radius="xl" mr={4}>
                {getInitials(user.name)}
              </Avatar>
            </Tooltip>
          ))}
          <Tooltip label={generateViewerListTooltip()} key={"otherViewers"}>
            <Avatar color={color} radius="xl">
              {`+${viewerList.length - VIEWER_COUNT}`}
            </Avatar>
          </Tooltip>
        </>
      )}
    </div>
  );
};

export default ViewerComponent;
